import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Scissors, 
  Building2,
  DollarSign,
  Send,
  Users,
  ChevronRight,
  Check,
  ArrowLeft,
  CreditCard,
  Calendar,
  Clock,
  TrendingUp,
  BarChart3,
  Shield,
  Bell,
  ArrowRight,
  CheckCircle2,
  Smartphone,
  Mail,
  MessageSquare,
  Star
} from "lucide-react";

const OwnerLandingPage = () => {
  const [showDemo, setShowDemo] = useState(false);

  const ownerFeatures = [
    {
      icon: DollarSign,
      title: "Booth Rent Collection",
      description: "Set monthly rent amounts and automatically collect payments from your booth renters."
    },
    {
      icon: Send,
      title: "Payment Links",
      description: "Send secure payment links via text or email. Get paid faster with instant notifications."
    },
    {
      icon: Users,
      title: "Renter Management",
      description: "Track all your booth renters, their payment history, and lease agreements."
    },
    {
      icon: BarChart3,
      title: "Revenue Dashboard",
      description: "See all your income at a glance - booth rent, your services, and total revenue."
    },
    {
      icon: Bell,
      title: "Payment Reminders",
      description: "Automatic reminders sent to renters before rent is due. Reduce late payments."
    },
    {
      icon: Shield,
      title: "Secure Transactions",
      description: "All payments are processed securely through Stripe with instant deposits."
    }
  ];

  const stylistFeatures = [
    "Create your service menu & pricing",
    "Set your working hours & availability",
    "Accept multiple payment methods",
    "Build your client base & loyalty program",
    "Showcase your portfolio",
    "Get automatic booking reminders"
  ];

  // How It Works for Owners
  const howItWorksSteps = [
    {
      step: 1,
      title: "Create Your Salon Profile",
      description: "Sign up as a Salon Owner. Add your salon name, location, and business details.",
      icon: Building2,
      color: "from-purple-500 to-indigo-500"
    },
    {
      step: 2,
      title: "Add Your Booth Renters",
      description: "Enter renter names, emails, phone numbers, booth assignments, and monthly rent amounts.",
      icon: Users,
      color: "from-blue-500 to-cyan-500"
    },
    {
      step: 3,
      title: "Set Payment Due Dates",
      description: "Choose which day of the month rent is due. System tracks all payments automatically.",
      icon: Calendar,
      color: "from-orange-500 to-amber-500"
    },
    {
      step: 4,
      title: "Send Payment Links",
      description: "One click sends a secure payment link via SMS or email. Renters pay instantly.",
      icon: Send,
      color: "from-green-500 to-emerald-500"
    },
    {
      step: 5,
      title: "Track & Collect",
      description: "Dashboard shows who's paid, who's late, and your total revenue. Automatic reminders do the nagging.",
      icon: TrendingUp,
      color: "from-pink-500 to-rose-500"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-indigo-50 to-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-purple-100/50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <ArrowLeft className="w-5 h-5 text-gray-500" />
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/30">
                <Scissors className="w-5 h-5 text-white" />
              </div>
              <span className="font-display font-bold text-xl text-gray-800">MyHairChair</span>
            </Link>

            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-gray-600 hover:text-purple-600">
                  Log In
                </Button>
              </Link>
              <Link to="/register?role=owner">
                <Button className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white shadow-lg shadow-purple-500/30">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white border border-purple-200 px-4 py-2 rounded-full mb-8 shadow-sm">
                <Building2 className="w-4 h-4 text-purple-500" />
                <span className="text-purple-700 text-sm font-medium">For Salon Owners</span>
              </div>
              
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-800 mb-6 leading-tight">
                Manage Your
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-indigo-500"> Salon Empire</span>
              </h1>
              
              <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                Collect booth rent, send payment links, and manage your team — plus all the tools 
                you need if you're also a practicing stylist.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-12">
                <Link to="/register?role=owner">
                  <Button className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white text-lg px-8 py-6 rounded-xl shadow-xl shadow-purple-500/30">
                    Start Managing
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  className="border-purple-300 text-gray-700 hover:bg-white text-lg px-8 py-6 rounded-xl"
                  onClick={() => setShowDemo(true)}
                >
                  See Demo
                </Button>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-4 border border-purple-100 shadow-sm">
                  <div className="text-2xl font-bold text-purple-600">$50K+</div>
                  <div className="text-xs text-gray-500">Rent Collected</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-purple-100 shadow-sm">
                  <div className="text-2xl font-bold text-purple-600">500+</div>
                  <div className="text-xs text-gray-500">Salon Owners</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-purple-100 shadow-sm">
                  <div className="text-2xl font-bold text-purple-600">98%</div>
                  <div className="text-xs text-gray-500">On-Time Payments</div>
                </div>
              </div>
            </div>

            {/* Hero Visual - Dashboard Preview */}
            <div className="relative">
              <div className="absolute -top-8 -left-8 w-64 h-64 bg-gradient-to-br from-purple-200 to-indigo-200 rounded-full blur-3xl opacity-50"></div>
              <div className="absolute -bottom-8 -right-8 w-64 h-64 bg-gradient-to-br from-indigo-200 to-purple-200 rounded-full blur-3xl opacity-50"></div>
              
              {/* Dashboard Preview Card */}
              <div className="relative bg-white rounded-3xl shadow-2xl p-6 border border-purple-100">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-bold text-gray-800">Revenue Overview</h3>
                  <span className="text-sm text-gray-500">This Month</span>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4">
                    <div className="text-sm text-gray-500 mb-1">Booth Rent</div>
                    <div className="text-2xl font-bold text-purple-600">$4,200</div>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4">
                    <div className="text-sm text-gray-500 mb-1">Services</div>
                    <div className="text-2xl font-bold text-green-600">$1,850</div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <img src="https://images.pexels.com/photos/3993467/pexels-photo-3993467.jpeg?auto=compress&w=40&h=40&fit=crop" alt="" className="w-10 h-10 rounded-full object-cover" />
                      <div>
                        <div className="font-medium text-sm">Keisha W.</div>
                        <div className="text-xs text-gray-500">Booth #1</div>
                      </div>
                    </div>
                    <span className="text-green-600 text-sm font-medium bg-green-100 px-2 py-1 rounded-full">Paid</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <img src="https://images.pexels.com/photos/3998429/pexels-photo-3998429.jpeg?auto=compress&w=40&h=40&fit=crop" alt="" className="w-10 h-10 rounded-full object-cover" />
                      <div>
                        <div className="font-medium text-sm">Carlos M.</div>
                        <div className="text-xs text-gray-500">Booth #2</div>
                      </div>
                    </div>
                    <span className="text-green-600 text-sm font-medium bg-green-100 px-2 py-1 rounded-full">Paid</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <img src="https://images.pexels.com/photos/7697673/pexels-photo-7697673.jpeg?auto=compress&w=40&h=40&fit=crop" alt="" className="w-10 h-10 rounded-full object-cover" />
                      <div>
                        <div className="font-medium text-sm">Marcus J.</div>
                        <div className="text-xs text-gray-500">Booth #3</div>
                      </div>
                    </div>
                    <Button size="sm" className="bg-purple-500 hover:bg-purple-600 text-white text-xs">
                      Send Reminder
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Demo Section */}
      {showDemo && (
        <section className="py-20 px-6 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative" id="demo">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-br from-purple-500/20 to-indigo-500/20 rounded-full blur-3xl"></div>
          <div className="max-w-5xl mx-auto relative">
            <div className="text-center mb-16">
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-4">
                How Booth Rent Collection Works
              </h2>
              <p className="text-gray-400 max-w-2xl mx-auto">
                Stop chasing rent payments. Let MyHairChair do the work for you.
              </p>
            </div>

            {/* Steps Flow */}
            <div className="space-y-6">
              {howItWorksSteps.map((item, index) => (
                <div key={item.step} className="relative">
                  <div className="flex items-start gap-6 bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                    <div className={`w-16 h-16 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg`}>
                      <item.icon className="w-8 h-8 text-white" />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-purple-400 text-sm font-bold">STEP {item.step}</span>
                        <h3 className="font-display text-xl font-bold text-white">
                          {item.title}
                        </h3>
                      </div>
                      <p className="text-gray-300">
                        {item.description}
                      </p>
                    </div>

                    <CheckCircle2 className="w-6 h-6 text-green-400 flex-shrink-0" />
                  </div>
                  
                  {index < howItWorksSteps.length - 1 && (
                    <div className="absolute left-8 top-full w-0.5 h-6 bg-gradient-to-b from-white/20 to-transparent"></div>
                  )}
                </div>
              ))}
            </div>

            {/* Interactive Demo Preview */}
            <div className="mt-16 bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
              <h3 className="text-white font-bold text-lg mb-6 text-center">
                📱 What Your Renter Sees
              </h3>
              <div className="max-w-sm mx-auto bg-white rounded-2xl p-6 shadow-2xl">
                <div className="text-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl mx-auto mb-3 flex items-center justify-center">
                    <Scissors className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="font-bold text-gray-800">MyHairChair</h4>
                  <p className="text-sm text-gray-500">Payment Request</p>
                </div>
                <div className="bg-purple-50 rounded-xl p-4 mb-4">
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">Booth Rent - March 2025</span>
                  </div>
                  <div className="text-3xl font-bold text-purple-600">$800.00</div>
                  <div className="text-sm text-gray-500">Due: March 1, 2025</div>
                </div>
                <Button className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 text-white py-6">
                  Pay Now
                </Button>
                <p className="text-xs text-center text-gray-400 mt-3">
                  Secure payment powered by Stripe
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center mt-12">
              <Link to="/register?role=owner">
                <Button className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-lg px-10 py-6 rounded-xl shadow-xl">
                  Start Collecting Rent Today
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <p className="text-gray-500 mt-4 text-sm">Free to start • No credit card required</p>
            </div>
          </div>
        </section>
      )}

      {/* Quick How It Works (always visible) */}
      {!showDemo && (
        <section className="py-16 px-6 bg-white/50">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-2xl font-bold text-gray-800 mb-8">
              Collect Rent in 3 Easy Steps
            </h2>
            <div className="flex flex-wrap justify-center items-center gap-8">
              {[
                { icon: Users, label: "Add Renters" },
                { icon: Send, label: "Send Link" },
                { icon: DollarSign, label: "Get Paid" }
              ].map((step, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-2xl flex items-center justify-center text-white shadow-lg">
                    <step.icon className="w-8 h-8" />
                  </div>
                  <span className="text-gray-700 font-semibold">{step.label}</span>
                  {i < 2 && <ArrowRight className="w-6 h-6 text-gray-300 mx-2" />}
                </div>
              ))}
            </div>
            <Button 
              variant="outline" 
              className="mt-8 border-purple-300"
              onClick={() => setShowDemo(true)}
            >
              See Full Demo
            </Button>
          </div>
        </section>
      )}

      {/* Owner Features Section */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
              Powerful Owner Tools
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Everything you need to manage your salon and collect booth rent.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {ownerFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-8 border border-purple-100 hover:shadow-xl hover:shadow-purple-100/50 hover:-translate-y-1 transition-all group"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-xl flex items-center justify-center mb-6 group-hover:from-purple-500 group-hover:to-indigo-500 transition-all">
                  <feature.icon className="w-7 h-7 text-purple-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-display text-xl font-semibold text-gray-800 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Also a Stylist Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-purple-500 to-indigo-500 rounded-3xl p-12 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="relative">
              <div className="flex items-center gap-3 mb-4">
                <Scissors className="w-8 h-8" />
                <span className="text-purple-200 font-medium">Also a Practicing Stylist?</span>
              </div>
              <h2 className="font-display text-3xl font-bold mb-4">
                All Stylist Features Included
              </h2>
              <p className="text-purple-100 mb-8">
                As a salon owner who also does hair, you get full access to all stylist features too.
              </p>
              <div className="grid md:grid-cols-2 gap-4 mb-10">
                {stylistFeatures.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4" />
                    </div>
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>
              <div className="text-center">
                <Link to="/register?role=owner">
                  <Button className="bg-white text-purple-600 hover:bg-purple-50 text-lg px-10 py-6 rounded-xl shadow-xl">
                    Get Started Free
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Payment Methods */}
      <section className="py-20 px-6 bg-white/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl font-bold text-gray-800 mb-4">
            Collect Rent Multiple Ways
          </h2>
          <p className="text-gray-600 mb-10">
            Give your booth renters flexible payment options
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {["Stripe", "PayPal", "Cash App", "Zelle"].map((method) => (
              <div key={method} className="bg-white rounded-xl p-6 border border-purple-100 hover:shadow-lg transition-shadow">
                <CreditCard className="w-10 h-10 text-purple-500 mx-auto mb-3" />
                <span className="font-medium text-gray-700">{method}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl p-8 border border-purple-100 shadow-lg">
            <div className="flex items-center gap-1 mb-4 justify-center">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
              ))}
            </div>
            <p className="text-xl text-gray-700 text-center mb-6 italic">
              "Before MyHairChair, I was chasing down rent every month. Now payments come in automatically. 
              My renters love how easy it is to pay, and I love not having awkward conversations about late rent."
            </p>
            <div className="flex items-center justify-center gap-4">
              <img 
                src="https://images.pexels.com/photos/3993467/pexels-photo-3993467.jpeg?auto=compress&w=60&h=60&fit=crop"
                alt="Salon Owner"
                className="w-14 h-14 rounded-full object-cover"
              />
              <div>
                <p className="font-semibold text-gray-800">Tanya Richardson</p>
                <p className="text-sm text-gray-500">Owner, Crown & Glory Salon • Atlanta, GA</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-purple-100 bg-white/50">
        <div className="max-w-6xl mx-auto text-center text-sm text-gray-500">
          © 2025 MyHairChair. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default OwnerLandingPage;
